import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { AbstractControl, ControlContainer, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ReactiveComponent } from '@app/shared/components/reactive-component';
import { ID } from '@app/utils/common';
import { ngValueAccessor } from '@app/utils/ng-value-accessor';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormControlTyped } from 'src/types/typed-forms';

@UntilDestroy()
@Component({
  selector: 'portal-inheritable-input',
  templateUrl: './inheritable-input.component.html',
  styles: [],
  providers: [ngValueAccessor(InheritableInputComponent)],
  standalone: false,
})
export class InheritableInputComponent extends ReactiveComponent implements OnChanges, OnDestroy, OnInit {
  @Input()
  activeValue: any;

  /**
   * If false then only the given input and label are displayed
   */
  @Input()
  canInherit: boolean = true;

  @Input()
  formControlName: string;

  @Input()
  inactiveValue: any = null;

  @Input()
  input: TemplateRef<any>;

  @Input()
  label: TemplateRef<any>;

  @Output()
  stateChange = new EventEmitter<boolean>();

  @Output()
  valueChange = new EventEmitter<{ inherited: boolean; value: any }>();

  public inheritValue?: boolean;

  public formControl: AbstractControl;
  public id: string = ID();
  public inheritedFormControl: FormControlTyped<boolean>;

  private form: UntypedFormGroup;
  private value: any;

  private updateInProgress: boolean = false;

  constructor(private controlContainer: ControlContainer) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges) {
    const inactiveValue = changes.inactiveValue?.currentValue;

    if (this.inheritValue === true) {
      this.updateFormControl(inactiveValue);
    }
  }

  public ngOnDestroy(): void {
    if (this.inheritValue === true) {
      this.updateFormControl(null);
    }

    this.form.removeControl(this.formControlName + 'Inherited');
  }

  public ngOnInit(): void {
    this.form = this.controlContainer.control as UntypedFormGroup;
    this.formControl = this.form.get(this.formControlName);

    if (this.activeValue == null) {
      this.activeValue = this.inactiveValue;
    }

    this.initializeInheritedFormControl();
  }

  public update(checked: boolean) {
    this.onTouched();

    this.inheritValue = checked;

    this.updateFormControl(this.inheritValue === true ? this.inactiveValue : this.activeValue);

    this.setFormControlState(checked);

    this.stateChange.emit(checked);
  }

  public writeValue(obj: any): void {
    this.value = obj;

    // Ensure that inheritValue is not overwritten (see the comment in updateFormControl for the
    // explanation of this check).
    if (this.updateInProgress === false) {
      this.inheritValue = obj == null;
      this.inheritedFormControl.setValue(this.inheritValue);

      if (this.inheritValue === true) {
        this.updateFormControl(this.inactiveValue);
      } else {
        this.valueChange.emit({
          inherited: this.inheritValue,
          value: this.inheritValue ? this.inactiveValue : obj,
        });
      }

      this.setFormControlState(this.inheritValue);
    }
  }

  private initializeInheritedFormControl() {
    const inheritedFormControlName = `${this.formControlName}Inherited`;

    if (this.form.get(inheritedFormControlName) == null) {
      this.form.addControl(inheritedFormControlName, new UntypedFormControl(this.inheritValue));
    }

    this.inheritedFormControl = this.form.get(inheritedFormControlName) as FormControlTyped<boolean>;

    // Watch the form control for changes because when the surrounding form is reset then there is no
    // value for the inherited form control (because the object containing the form does not have the
    // corresponding property). So when being reset then the inherited form control gets the value
    // null and this is incorrect. In this case we reset the value back to this.inheritedValue.
    this.inheritedFormControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value == null) {
        this.inheritedFormControl.setValue(this.inheritValue, {
          emitEvent: false,
          onlySelf: true,
        });

        this.stateChange.emit(this.inheritValue);
      }
    });
  }

  private setFormControlState(active: boolean) {
    if (active === true) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }

  private updateFormControl(value: any) {
    this.updateInProgress = true;

    this.onChange(value);
    this.onTouched();

    // Angular does not update any form controls when a new value via onChange is emitted. So we
    // have to set the value manually on the form control and ensure in the writeValue method that
    // the inheritValue property is not overwritten.
    this.formControl.setValue(value, {
      emitEvent: false,
      onlySelf: true,
      emitModelToViewChange: true,
      emitViewToModelChange: false,
    });

    this.valueChange.emit({
      inherited: this.inheritValue,
      value: this.formControl.value,
    });

    this.updateInProgress = false;
  }
}
